import { useNavigate, useParams, useLocation } from "react-router-dom";
import MainContainer from "../../layout/MainContainer";
import {
  Box,
  TextField,
  Button,
  CardMedia,
  Input,
  IconButton,
  MenuItem,
  Select,
  FormHelperText,
  Grid,
  Typography,
  FormControl,
  InputLabel,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import React, { ChangeEvent, useEffect, useState, useMemo } from "react";
import {
  useAddUserMutation,
  useUpdateUserMutation,
  useLazyGetUsersByIdQuery,
} from "../../services/customer";
import { useFormik } from "formik";
import * as Yup from "yup";
import { UploadMedia } from "../../utils/mediaUpload";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/material.css";
import { showError, showToast } from "../../constants/toasts";
import { removeFalsyKeys } from "../../utils/nullKey";
import dayjs from "dayjs";

const USER_ROLE = {
  USER: "USER",
  OWNER: "OWNER",
};

const AddCustomer = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const [AddUser] = useAddUserMutation();
  const [updateUser] = useUpdateUserMutation();
  const [getUserById] = useLazyGetUsersByIdQuery();
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [openDocView, setOpenDocView] = useState(false);
  const [docViewImage, setDocViewImage] = useState("");
  const intitalValues = useMemo(() => {
    return {
      firstName: user?.firstName || "",
      middleName: user?.middleName || "",
      lastName: user?.lastName || "",
      phone: user?.phoneNumber || "",
      email: user?.email || "",
      countryCode: user?.countryCode || "+91",
      callingCode: user?.callingCode || "IN",
      profileImage: user?.image || "",
      address1: user?.addressLineOne || "",
      address2: user?.addressLineTwo || "",
      country: user?.country || "",
      state: user?.state || "",
      city: user?.city || "",
      zipCode: user?.zipCode || "",
      isEmailVerified: user?.isEmailVerified || false,
      isPhoneVerified: user?.phoneVerified || false,
      document: user?.documentImage || "",
      emergencyCountryCode: user?.emergencyCountryCode || "+91",
      emergencyCallingCode: user?.emergencyCallingCode || "IN",
      emergencyContactNumber: user?.emergencyContactNumber || "",
      dob: user?.dob ? dayjs(user?.dob) : null,
      // Owner specific
      nationalIdFront: (user as any)?.nationalId?.frontSide || "",
      nationalIdBack: (user as any)?.nationalId?.backSide || "",
      spaceProof: (user as any)?.spaceProof || "",
    };
  }, [user]);

  const getDataByIdList = async () => {
    try {
      setLoading(true);
      if (!id) return;
      const response = await getUserById({ id }).unwrap();
      if (response.statusCode === 200) {
        setUser(response.data);
      }
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      showError(error?.message || error?.data?.message);
    }
  };

  useEffect(() => {
    if (id) {
      getDataByIdList();
    }
  }, [id]);

  useEffect(() => {
    if (!id) {
      const role = location.state?.tab === 1 ? "OWNER" : "USER";
      // Set default role for new users
    }
  }, [id, location.state]);

  const handleImageUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target?.files?.[0];
    if (!file) return;
    const imageUrl = (await UploadMedia(file)) as any;
    if (imageUrl?.data) {
      formik.setFieldValue("profileImage", imageUrl.data);
    }
  };

  const handleDocumentUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target?.files?.[0];
    if (!file) return;
    const imageUrl = (await UploadMedia(file)) as any;
    if (imageUrl?.data) {
      formik.setFieldValue("document", imageUrl.data);
    }
  };

  const handleFileUploadToField = async (
    event: React.ChangeEvent<HTMLInputElement>,
    fieldName: string
  ) => {
    const file = event.target?.files?.[0];
    if (!file) return;
    const imageUrl = (await UploadMedia(file)) as any;
    if (imageUrl?.data) {
      formik.setFieldValue(fieldName, imageUrl.data);
    }
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: intitalValues,
    validationSchema: Yup.object({
      firstName: Yup.string()
        .required(
          user?.role === USER_ROLE.USER
            ? "First name is required"
            : "first name is required"
        )
        .min(
          3,
          user?.role === USER_ROLE.USER
            ? "First name must be at least 3 characters"
            : "first name must be at least 3 characters"
        ),
      middleName: Yup.string().required("Middle name is required"),
      lastName: Yup.string().required("Last name is required"),
      email: Yup.string()
        .required("Email is required")
        .matches(
          /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
          "Enter a valid email address"
        ),
      phone: Yup.string()
        .required("Phone number is required")
        .min(8, "Phone number must be at least 8 digits"),
      ...(user?.role === USER_ROLE.USER && {
        address1: Yup.string().required("Address line 1 is required"),
        country: Yup.string().required("Country is required"),
        state: Yup.string().required("State is required"),
        city: Yup.string().required("City is required"),
        zipCode: Yup.string().required("Zip Code is required"),
        dob: Yup.string().required("Date of birth is required"),
        document: Yup.string().required("Identity Verification is required"),
      }),
    }),
    onSubmit: async (values) => {
      // if (values.phone != user?.phoneNumber || values.email != user?.email) {
      //   showError(
      //     values.phone != user?.phoneNumber
      //       ? "Please verify Phone number first"
      //       : "Please verify Email first"
      //   );
      //   return;
      // } else if (
      //   values?.emergencyContactNumber?.length &&
      //   values?.emergencyContactNumber?.length < 8
      // ) {
      //   showError("Please enter valid emergency contact number");
      //   return;
      // }
      const computedRole = id
        ? user?.role
        : location.state?.tab === 1
        ? USER_ROLE.OWNER
        : USER_ROLE.USER;
      const body: any = {
        image: formik.values.profileImage || undefined,
        firstName: values?.firstName,
        middleName: values?.middleName,
        lastName: values?.lastName,
        phoneNumber: `${(values?.countryCode || "").replace(/^([^+])/, "+$1")}${
          values?.phone || ""
        }`,
        countryCode: (values?.countryCode || "").startsWith("+")
          ? values.countryCode
          : `+${values?.countryCode || ""}`,
        callingCode: values?.callingCode,
        email: values?.email,
      };
      if (!id) {
        body.role = computedRole;
      }

      // When updating, only send fields that actually changed
      if (id) {
        const original: any = {
          image: user?.image || undefined,
          firstName: user?.firstName,
          middleName: user?.middleName,
          lastName: user?.lastName,
          phoneNumber: user?.phoneNumber,
          countryCode: user?.countryCode,
          callingCode: user?.callingCode,
          email: user?.email,
        };

        if (computedRole === USER_ROLE.USER) {
          original.addressLineOne = user?.addressLineOne;
          original.country = user?.country;
          original.state = user?.state;
          original.city = user?.city;
          original.addressLineTwo = user?.addressLineTwo || undefined;
          original.zipCode = user?.zipCode;
          original.dob = user?.dob ? dayjs(user?.dob).toISOString() : undefined;
          original.documentImage = user?.documentImage || undefined;
          original.emergencyContactNumber =
            user?.emergencyContactNumber || undefined;
          original.emergencyCountryCode =
            user?.emergencyCountryCode || undefined;
          original.emergencyCallingCode =
            user?.emergencyCallingCode || undefined;
        } else if (computedRole === USER_ROLE.OWNER) {
          // Owner specific fields
          if (user?.nationalId) {
            original.nationalId = {
              frontSide: user?.nationalId?.frontSide || "",
              backSide: user?.nationalId?.backSide || "",
            };
          }
          if ((user as any)?.spaceProof) {
            original.spaceProof = (user as any)?.spaceProof;
          }
        }

        // Remove unchanged or undefined primitive fields
        Object.keys(body).forEach((key) => {
          const newVal = (body as any)[key];
          const oldVal = (original as any)[key];
          if (newVal === undefined || newVal === oldVal) {
            delete (body as any)[key];
          }
        });

        // Handle nested objects diffs (e.g., nationalId)
        if ((body as any).nationalId) {
          const newNI = (body as any).nationalId;
          const oldNI = (original as any).nationalId || {};
          const sameFront = newNI.frontSide === oldNI.frontSide;
          const sameBack = newNI.backSide === oldNI.backSide;
          if (sameFront && sameBack) {
            delete (body as any).nationalId;
          }
        }

        // If nothing changed, short-circuit to avoid unnecessary request
        if (Object.keys(body).length === 0) {
          showToast("No changes to update");
          return;
        }
      }

      if (computedRole === USER_ROLE.OWNER) {
        if (
          (values as any)?.nationalIdFront ||
          (values as any)?.nationalIdBack
        ) {
          body.nationalId = {
            frontSide: (values as any)?.nationalIdFront || "",
            backSide: (values as any)?.nationalIdBack || "",
          };
        }
        if ((values as any)?.spaceProof) {
          body.spaceProof = (values as any)?.spaceProof;
        }
      } else if (computedRole === USER_ROLE.USER) {
        body.addressLineOne = values.address1;
        body.country = values.country;
        body.state = values.state;
        body.city = values.city;
        body.addressLineTwo = values.address2 || undefined;
        body.zipCode = values.zipCode;
        body.dob = values?.dob ? dayjs(values.dob).toISOString() : undefined;
        body.documentImage = values?.document || undefined;
        if (values?.emergencyContactNumber) {
          body.emergencyContactNumber = values.emergencyContactNumber;
          body.emergencyCountryCode = (
            values?.emergencyCountryCode || ""
          ).startsWith("+")
            ? values.emergencyCountryCode
            : `+${values?.emergencyCountryCode || ""}`;
          body.emergencyCallingCode = values?.emergencyCallingCode;
        }
      }
      console.log(body, "body");

      try {
        let response;
        if (id) {
          response = await updateUser({ body, id }).unwrap();
        } else {
          response = await AddUser({ body }).unwrap();
        }

        if (response?.statusCode === 200) {
          showToast(
            id ? "User updated successfully" : "User added successfully"
          );
          navigate("/manage-customers");
        }
      } catch (err: any) {
        showError(err?.data?.message || "Something went wrong");
      }
    },
  });

  const handlePhoneChange = (phoneValue: any, country: any) => {
    const phoneWithoutCode = phoneValue?.replace(country.dialCode, "");
    formik.setFieldValue("phone", phoneWithoutCode);
    const codeWithPlus = country?.dialCode?.toString().startsWith("+")
      ? country.dialCode
      : `+${country.dialCode}`;
    formik.setFieldValue("countryCode", codeWithPlus);
    formik.setFieldValue("callingCode", country.countryCode?.toUpperCase());
  };

  const handleEmergencyPhoneChange = (phoneValue: any, country: any) => {
    const phoneWithoutCode = phoneValue?.replace(country.dialCode, "");
    formik.setFieldValue("emergencyContactNumber", phoneWithoutCode);
    const codeWithPlus = country?.dialCode?.toString().startsWith("+")
      ? country.dialCode
      : `+${country.dialCode}`;
    formik.setFieldValue("emergencyCountryCode", codeWithPlus);
    formik.setFieldValue(
      "emergencyCallingCode",
      country.countryCode?.toUpperCase()
    );
  };

  const currentRole = id
    ? user?.role
    : location.state?.tab === 1
    ? USER_ROLE.OWNER
    : USER_ROLE.USER;

  return (
    <MainContainer>
      <div className="main_title">
        <h1>
          {id ? "Edit" : "Add"}{" "}
          {currentRole === USER_ROLE.OWNER ? "Owner" : "User"}
        </h1>
        <p>
          <span onClick={() => navigate("/dashboard")}>Dashboard</span>
          {"-"}
          <span
            onClick={() =>
              navigate("/manage-customers", {
                state:
                  currentRole === USER_ROLE.OWNER ? { tab: 1 } : { tab: 0 },
              })
            }
          >
            Manage User
          </span>
        </p>
      </div>
      <div className="cards">
        <div className="form">
          <div className="gap_p">
            <div className="control_group w_100">
              <label>Upload Profile Image</label>
              {formik.values.profileImage ? (
                <div className="upload_image">
                  <div className="upload_image_holder">
                    <figure>
                      <CardMedia
                        component="img"
                        image={formik.values.profileImage}
                        alt="Profile"
                      />
                    </figure>
                    <CloseIcon
                      onClick={() => {
                        formik.setFieldValue("profileImage", "");
                      }}
                    />
                  </div>
                </div>
              ) : (
                <label className="upload_image" htmlFor="profile-image-file">
                  <Input
                    id="profile-image-file"
                    type="file"
                    sx={{ display: "none" }}
                    inputProps={{ accept: "image/png,image/jpeg" }}
                    onChange={(event) => {
                      handleImageUpload(event as any);
                      event.target.value = "";
                    }}
                  />
                  <span className="upload_image_holder">
                    <figure className="add_img">
                      <img src="/static/images/placeholder.png" alt="icon" />
                      <span className="upload_img_icon">
                        <img src="/static/images/edit2_icon.svg" alt="edit" />
                      </span>
                    </figure>
                  </span>
                </label>
              )}
            </div>

            <form className="form" onSubmit={formik.handleSubmit}>
              <div className="gap_p">
                {/* Name Fields */}
                <div className="control_group w_33">
                  <label>First Name</label>
                  <TextField
                    variant="outlined"
                    placeholder="Enter first name"
                    className="text_field"
                    fullWidth
                    hiddenLabel
                    name="firstName"
                    id="firstName"
                    value={formik.values.firstName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    helperText={
                      formik.touched.firstName &&
                      (formik.errors.firstName as any)
                    }
                    error={
                      formik.touched.firstName &&
                      Boolean(formik.errors.firstName)
                    }
                  />
                </div>

                <div className="control_group w_33">
                  <label>Middle Name</label>
                  <TextField
                    variant="outlined"
                    placeholder="Enter middle name"
                    className="text_field"
                    fullWidth
                    hiddenLabel
                    name="middleName"
                    id="middleName"
                    value={formik.values.middleName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    helperText={
                      formik.touched.middleName &&
                      (formik.errors.middleName as any)
                    }
                    error={
                      formik.touched.middleName &&
                      Boolean(formik.errors.middleName)
                    }
                  />
                </div>

                <div className="control_group w_33">
                  <label>Last Name</label>
                  <TextField
                    variant="outlined"
                    placeholder="Enter last name"
                    className="text_field"
                    fullWidth
                    hiddenLabel
                    name="lastName"
                    id="lastName"
                    value={formik.values.lastName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    helperText={
                      formik.touched.lastName && (formik.errors.lastName as any)
                    }
                    error={
                      formik.touched.lastName && Boolean(formik.errors.lastName)
                    }
                  />
                </div>

                {/* Contact Fields */}
                <div className="control_group w_50">
                  <label>Email</label>
                  <TextField
                    variant="outlined"
                    placeholder="Enter email address"
                    className="text_field"
                    fullWidth
                    hiddenLabel
                    name="email"
                    id="email"
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    helperText={
                      formik.touched.email && (formik.errors.email as any)
                    }
                    error={formik.touched.email && Boolean(formik.errors.email)}
                  />
                </div>

                <div className="control_group w_50">
                  <label>Phone Number</label>
                  <PhoneInput
                    placeholder="Enter phone number"
                    enableSearch
                    value={formik.values.countryCode + formik.values.phone}
                    country={"in"}
                    onChange={(phone, country) =>
                      handlePhoneChange(phone, country)
                    }
                    inputStyle={{
                      width: "100%",
                      border:
                        formik.touched.phone && formik.errors.phone
                          ? "1px solid #d32f2f"
                          : "1px solid #ccc",
                    }}
                  />
                  {formik.touched.phone && formik.errors.phone && (
                    <FormHelperText error>
                      {formik.errors.phone as any}
                    </FormHelperText>
                  )}
                </div>
                {/* Address Fields - Only for Users */}
                {currentRole === USER_ROLE.USER && (
                  <>
                    <div className="control_group w_100">
                      <label>Address Line 1</label>
                      <TextField
                        variant="outlined"
                        placeholder="Enter address line 1"
                        className="text_field"
                        fullWidth
                        hiddenLabel
                        name="address1"
                        id="address1"
                        value={formik.values.address1}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        helperText={
                          formik.touched.address1 &&
                          (formik.errors.address1 as any)
                        }
                        error={
                          formik.touched.address1 &&
                          Boolean(formik.errors.address1)
                        }
                      />
                    </div>

                    <div className="control_group w_100">
                      <label>Address Line 2 (Optional)</label>
                      <TextField
                        variant="outlined"
                        placeholder="Enter address line 2"
                        className="text_field"
                        fullWidth
                        hiddenLabel
                        name="address2"
                        id="address2"
                        value={formik.values.address2}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                      />
                    </div>

                    <div className="control_group w_25">
                      <label>Country</label>
                      <TextField
                        variant="outlined"
                        placeholder="Enter country"
                        className="text_field"
                        fullWidth
                        hiddenLabel
                        name="country"
                        id="country"
                        value={formik.values.country}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        helperText={
                          formik.touched.country &&
                          (formik.errors.country as any)
                        }
                        error={
                          formik.touched.country &&
                          Boolean(formik.errors.country)
                        }
                      />
                    </div>

                    <div className="control_group w_25">
                      <label>State</label>
                      <TextField
                        variant="outlined"
                        placeholder="Enter state"
                        className="text_field"
                        fullWidth
                        hiddenLabel
                        name="state"
                        id="state"
                        value={formik.values.state}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        helperText={
                          formik.touched.state && (formik.errors.state as any)
                        }
                        error={
                          formik.touched.state && Boolean(formik.errors.state)
                        }
                      />
                    </div>

                    <div className="control_group w_25">
                      <label>City</label>
                      <TextField
                        variant="outlined"
                        placeholder="Enter city"
                        className="text_field"
                        fullWidth
                        hiddenLabel
                        name="city"
                        id="city"
                        value={formik.values.city}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        helperText={
                          formik.touched.city && (formik.errors.city as any)
                        }
                        error={
                          formik.touched.city && Boolean(formik.errors.city)
                        }
                      />
                    </div>

                    <div className="control_group w_25">
                      <label>Zip Code</label>
                      <TextField
                        variant="outlined"
                        placeholder="Enter zip code"
                        className="text_field"
                        fullWidth
                        hiddenLabel
                        name="zipCode"
                        id="zipCode"
                        value={formik.values.zipCode}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        helperText={
                          formik.touched.zipCode &&
                          (formik.errors.zipCode as any)
                        }
                        error={
                          formik.touched.zipCode &&
                          Boolean(formik.errors.zipCode)
                        }
                      />
                    </div>

                    <div className="control_group w_50">
                      <label>Date of Birth</label>
                      <TextField
                        variant="outlined"
                        type="date"
                        className="text_field"
                        fullWidth
                        hiddenLabel
                        name="dob"
                        id="dob"
                        value={
                          formik.values.dob
                            ? dayjs(formik.values.dob).format("YYYY-MM-DD")
                            : ""
                        }
                        onChange={(e) =>
                          formik.setFieldValue("dob", dayjs(e.target.value))
                        }
                        onBlur={formik.handleBlur}
                        helperText={formik.touched.dob && formik.errors.dob}
                        error={formik.touched.dob && Boolean(formik.errors.dob)}
                        InputLabelProps={{
                          shrink: true,
                        }}
                      />
                    </div>
                  </>
                )}

                {/* Owner Documents - Only for Owners */}
                {currentRole === USER_ROLE.OWNER && (
                  <>
                    <div className="control_group w_50">
                      <label>National ID (Front)</label>
                      {formik.values.nationalIdFront ? (
                        <div
                          style={{
                            width: "100%",
                            maxWidth: 320,
                            height: 180,
                            border: "2px dashed #e71c57",
                            borderRadius: 12,
                            position: "relative",
                            // overflow: "hidden",
                            background: "#fff",
                          }}
                        >
                          <CardMedia
                            component="img"
                            image={formik.values.nationalIdFront}
                            alt="National ID Front"
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                          />
                          <IconButton
                            onClick={() =>
                              formik.setFieldValue("nationalIdFront", "")
                            }
                            style={{
                              position: "absolute",
                              top: -8,
                              right: -8,
                              background: "#e71c57",
                              color: "#fff",
                              width: 28,
                              height: 28,
                            }}
                          >
                            <CloseIcon style={{ fontSize: 18 }} />
                          </IconButton>
                        </div>
                      ) : (
                        <label
                          htmlFor="national-front-file"
                          style={{
                            width: "100%",
                            maxWidth: 320,
                            height: 180,
                            border: "2px dashed #e71c57",
                            borderRadius: 12,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            background: "#fff",
                            cursor: "pointer",
                          }}
                        >
                          <Input
                            id="national-front-file"
                            type="file"
                            sx={{ display: "none" }}
                            inputProps={{ accept: "image/png,image/jpeg" }}
                            onChange={(event) => {
                              handleFileUploadToField(
                                event as any,
                                "nationalIdFront"
                              );
                              (event.target as any).value = "";
                            }}
                          />
                          <div style={{ textAlign: "center" }}>
                            <svg
                              width="32"
                              height="32"
                              viewBox="0 0 24 24"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                              style={{ color: "#e71c57" }}
                            >
                              <path
                                d="M4 6a2 2 0 0 1 2-2h7a2 2 0 0 1 2 2v1h1a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6z"
                                stroke="#e71c57"
                                strokeWidth="1.6"
                              />
                              <path
                                d="M7 14l2.5-3 2.5 3 2-2 3 4H7z"
                                fill="#e71c57"
                                opacity=".25"
                              />
                              <path
                                d="M18 6v3M19.5 7.5h-3"
                                stroke="#e71c57"
                                strokeWidth="1.6"
                                strokeLinecap="round"
                              />
                            </svg>
                            <div
                              style={{
                                marginTop: 8,
                                color: "#555",
                                fontWeight: 500,
                              }}
                            >
                              Doc, Image
                            </div>
                          </div>
                        </label>
                      )}
                    </div>

                    <div className="control_group w_50">
                      <label>National ID (Back)</label>
                      {formik.values.nationalIdBack ? (
                        <div
                          style={{
                            width: "100%",
                            maxWidth: 320,
                            height: 180,
                            border: "2px dashed #e71c57",
                            borderRadius: 12,
                            position: "relative",
                            // overflow: "hidden",
                            background: "#fff",
                          }}
                        >
                          <CardMedia
                            component="img"
                            image={formik.values.nationalIdBack}
                            alt="National ID Back"
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                          />
                          <IconButton
                            onClick={() =>
                              formik.setFieldValue("nationalIdBack", "")
                            }
                            style={{
                              position: "absolute",
                              top: -8,
                              right: -8,
                              background: "#e71c57",
                              color: "#fff",
                              width: 28,
                              height: 28,
                            }}
                          >
                            <CloseIcon style={{ fontSize: 18 }} />
                          </IconButton>
                        </div>
                      ) : (
                        <label
                          htmlFor="national-back-file"
                          style={{
                            width: "100%",
                            maxWidth: 320,
                            height: 180,
                            border: "2px dashed #e71c57",
                            borderRadius: 12,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            background: "#fff",
                            cursor: "pointer",
                          }}
                        >
                          <Input
                            id="national-back-file"
                            type="file"
                            sx={{ display: "none" }}
                            inputProps={{ accept: "image/png,image/jpeg" }}
                            onChange={(event) => {
                              handleFileUploadToField(
                                event as any,
                                "nationalIdBack"
                              );
                              (event.target as any).value = "";
                            }}
                          />
                          <div style={{ textAlign: "center" }}>
                            <svg
                              width="32"
                              height="32"
                              viewBox="0 0 24 24"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                              style={{ color: "#e71c57" }}
                            >
                              <path
                                d="M4 6a2 2 0 0 1 2-2h7a2 2 0 0 1 2 2v1h1a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6z"
                                stroke="#e71c57"
                                strokeWidth="1.6"
                              />
                              <path
                                d="M7 14l2.5-3 2.5 3 2-2 3 4H7z"
                                fill="#e71c57"
                                opacity=".25"
                              />
                              <path
                                d="M18 6v3M19.5 7.5h-3"
                                stroke="#e71c57"
                                strokeWidth="1.6"
                                strokeLinecap="round"
                              />
                            </svg>
                            <div
                              style={{
                                marginTop: 8,
                                color: "#555",
                                fontWeight: 500,
                              }}
                            >
                              Doc, Image
                            </div>
                          </div>
                        </label>
                      )}
                    </div>

                    <div className="control_group w_50">
                      <label>Space Proof Document</label>
                      {formik.values.spaceProof ? (
                        <div
                          style={{
                            width: "100%",
                            maxWidth: 320,
                            height: 180,
                            border: "2px dashed #e71c57",
                            borderRadius: 12,
                            position: "relative",
                            // overflow: "hidden",
                            background: "#fff",
                          }}
                        >
                          <CardMedia
                            component="img"
                            image={formik.values.spaceProof}
                            alt="Space Proof"
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                          />
                          <IconButton
                            onClick={() =>
                              formik.setFieldValue("spaceProof", "")
                            }
                            style={{
                              position: "absolute",
                              top: -8,
                              right: -8,
                              background: "#e71c57",
                              color: "#fff",
                              width: 28,
                              height: 28,
                            }}
                          >
                            <CloseIcon style={{ fontSize: 18 }} />
                          </IconButton>
                        </div>
                      ) : (
                        <label
                          htmlFor="space-proof-file"
                          style={{
                            width: "100%",
                            maxWidth: 320,
                            height: 180,
                            border: "2px dashed #e71c57",
                            borderRadius: 12,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            background: "#fff",
                            cursor: "pointer",
                          }}
                        >
                          <Input
                            id="space-proof-file"
                            type="file"
                            sx={{ display: "none" }}
                            inputProps={{ accept: "image/png,image/jpeg" }}
                            onChange={(event) => {
                              handleFileUploadToField(
                                event as any,
                                "spaceProof"
                              );
                              (event.target as any).value = "";
                            }}
                          />
                          <div style={{ textAlign: "center" }}>
                            <svg
                              width="32"
                              height="32"
                              viewBox="0 0 24 24"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                              style={{ color: "#e71c57" }}
                            >
                              <path
                                d="M4 6a2 2 0 0 1 2-2h7a2 2 0 0 1 2 2v1h1a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6z"
                                stroke="#e71c57"
                                strokeWidth="1.6"
                              />
                              <path
                                d="M7 14l2.5-3 2.5 3 2-2 3 4H7z"
                                fill="#e71c57"
                                opacity=".25"
                              />
                              <path
                                d="M18 6v3M19.5 7.5h-3"
                                stroke="#e71c57"
                                strokeWidth="1.6"
                                strokeLinecap="round"
                              />
                            </svg>
                            <div
                              style={{
                                marginTop: 8,
                                color: "#555",
                                fontWeight: 500,
                              }}
                            >
                              Doc, Image
                            </div>
                          </div>
                        </label>
                      )}
                    </div>
                  </>
                )}

                {currentRole === USER_ROLE.USER && (
                  <>
                    {/* Emergency Contact Fields */}
                    <div className="control_group w_50">
                      <label>Emergency Contact Number</label>
                      <PhoneInput
                        placeholder="Enter emergency contact number"
                        enableSearch
                        value={
                          formik.values.emergencyCountryCode +
                          formik.values.emergencyContactNumber
                        }
                        country={"in"}
                        onChange={(phone, country) =>
                          handleEmergencyPhoneChange(phone, country)
                        }
                        inputStyle={{
                          width: "100%",
                          border: "1px solid #ccc",
                        }}
                      />
                      {/* Identity Verification Document - Only for Users */}
                      <div className="control_group w_50">
                        <label>Identity Verification</label>
                        {formik.values.document ? (
                          <div
                            style={{
                              width: "100%",
                              maxWidth: 320,
                              height: 180,
                              border: "2px dashed #e71c57",
                              borderRadius: 12,
                              position: "relative",
                              // overflow: "hidden",
                              background: "#fff",
                            }}
                          >
                            <CardMedia
                              component="img"
                              image={formik.values.document}
                              alt="Identity Document"
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                              }}
                            />
                            <IconButton
                              onClick={() =>
                                formik.setFieldValue("document", "")
                              }
                              style={{
                                position: "absolute",
                                top: -8,
                                right: -8,
                                background: "#e71c57",
                                color: "#fff",
                                width: 28,
                                height: 28,
                              }}
                            >
                              <CloseIcon style={{ fontSize: 18 }} />
                            </IconButton>
                          </div>
                        ) : (
                          <label
                            htmlFor="document-file"
                            style={{
                              width: "100%",
                              maxWidth: 320,
                              height: 180,
                              border: "2px dashed #e71c57",
                              borderRadius: 12,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              background: "#fff",
                              cursor: "pointer",
                            }}
                          >
                            <Input
                              id="document-file"
                              type="file"
                              sx={{ display: "none" }}
                              inputProps={{ accept: "image/png,image/jpeg" }}
                              onChange={(event) => {
                                handleDocumentUpload(event as any);
                                (event.target as any).value = "";
                              }}
                            />
                            <div style={{ textAlign: "center" }}>
                              <svg
                                width="32"
                                height="32"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                                style={{ color: "#e71c57" }}
                              >
                                <path
                                  d="M4 6a2 2 0 0 1 2-2h7a2 2 0 0 1 2 2v1h1a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6z"
                                  stroke="#e71c57"
                                  strokeWidth="1.6"
                                />
                                <path
                                  d="M7 14l2.5-3 2.5 3 2-2 3 4H7z"
                                  fill="#e71c57"
                                  opacity=".25"
                                />
                                <path
                                  d="M18 6v3M19.5 7.5h-3"
                                  stroke="#e71c57"
                                  strokeWidth="1.6"
                                  strokeLinecap="round"
                                />
                              </svg>
                              <div
                                style={{
                                  marginTop: 8,
                                  color: "#555",
                                  fontWeight: 500,
                                }}
                              >
                                Doc, Image
                              </div>
                            </div>
                          </label>
                        )}
                        {formik.touched.document && formik.errors.document && (
                          <FormHelperText error>
                            {formik.errors.document as any}
                          </FormHelperText>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>

              <div className="form_btn end">
                <Button
                  className="btn btn_primary"
                  type="submit"
                  disabled={formik.isSubmitting}
                >
                  {formik.isSubmitting ? "Saving..." : "Save"}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </MainContainer>
  );
};

export default AddCustomer;
